<script lang="ts" setup>
import { Refresh } from "@element-plus/icons-vue";
import { computed, onMounted, ref } from "vue";

import {
  FormDefinitionChangeDetailDto,
  FormDefinitionChangeDto,
  FormDefinitionChangeKind,
  FormDefinitionChangeType,
} from "~/api";
import { apiClient } from "~/utils/apiClient";
import { formatDate } from "~/utils/dateUtils";
import { showError } from "~/utils/handleAPIError";

const props = defineProps<{
  formDefinitionGuid: string;
}>();

const changes = ref<FormDefinitionChangeDto[]>([]);
const loading = ref(false);

async function loadHistory() {
  if (!props.formDefinitionGuid) {
    return;
  }
  loading.value = true;
  try {
    changes.value = await apiClient.formDefinition.getFormDefinitionHistory(
      props.formDefinitionGuid,
    );
  } catch (error) {
    showError(error);
  } finally {
    loading.value = false;
  }
}

// The tab is lazy — it mounts on first open, so fetch on mount.
onMounted(loadHistory);

const kindLabels: Record<FormDefinitionChangeKind, string> = {
  [FormDefinitionChangeKind.CREATED]: "Created",
  [FormDefinitionChangeKind.UPDATED]: "Saved",
  [FormDefinitionChangeKind.DRAFT_STARTED]: "Draft started",
  [FormDefinitionChangeKind.DRAFT_EDITED]: "Draft edit",
  [FormDefinitionChangeKind.DRAFT_COMMITTED]: "Draft committed",
  [FormDefinitionChangeKind.DRAFT_DISCARDED]: "Draft discarded",
};

function kindLabel(kind: FormDefinitionChangeKind | undefined): string {
  return kind ? kindLabels[kind] : "Change";
}

/** el-tag type for the change kind. */
function kindTagType(
  kind: FormDefinitionChangeKind | undefined,
): "primary" | "success" | "warning" | "danger" | "info" {
  switch (kind) {
    case FormDefinitionChangeKind.CREATED:
    case FormDefinitionChangeKind.DRAFT_STARTED:
      return "primary";
    case FormDefinitionChangeKind.DRAFT_COMMITTED:
    case FormDefinitionChangeKind.UPDATED:
      return "success";
    case FormDefinitionChangeKind.DRAFT_DISCARDED:
      return "danger";
    default:
      return "info";
  }
}

/** el-tag type for a single change detail. */
function detailTagType(
  type: FormDefinitionChangeType | undefined,
): "success" | "warning" | "danger" | "info" {
  switch (type) {
    case FormDefinitionChangeType.ELEMENT_ADDED:
      return "success";
    case FormDefinitionChangeType.ELEMENT_CHANGED:
    case FormDefinitionChangeType.RULE_CHANGED:
      return "warning";
    case FormDefinitionChangeType.ELEMENT_REMOVED:
      return "danger";
    default:
      return "info";
  }
}

interface FieldDiff {
  field: string;
  before: string;
  after: string;
}

/** Renders a value for the diff table (objects/arrays as compact JSON). */
function displayValue(value: unknown): string {
  if (value === null || value === undefined) {
    return "—";
  }
  if (typeof value === "object") {
    return JSON.stringify(value);
  }
  return String(value);
}

// Fields that are noise in a human diff (ordering/identity plumbing).
const IGNORED_FIELDS = new Set([
  "formOrder",
  "guid",
  "formDefinitionGuid",
  "parentGuid",
]);

/**
 * Computes the changed fields between the before/after element JSON of an
 * ElementChanged detail. Returns [] if either side is missing/unparseable.
 */
function fieldDiffs(detail: FormDefinitionChangeDetailDto): FieldDiff[] {
  if (!detail.beforeJson || !detail.afterJson) {
    return [];
  }
  let before: Record<string, unknown>;
  let after: Record<string, unknown>;
  try {
    before = JSON.parse(detail.beforeJson);
    after = JSON.parse(detail.afterJson);
  } catch {
    return [];
  }
  const keys = new Set([...Object.keys(before), ...Object.keys(after)]);
  const diffs: FieldDiff[] = [];
  for (const key of keys) {
    if (IGNORED_FIELDS.has(key)) {
      continue;
    }
    const b = displayValue(before[key]);
    const a = displayValue(after[key]);
    if (b !== a) {
      diffs.push({ field: key, before: b, after: a });
    }
  }
  return diffs;
}

// --- Rule diffs -----------------------------------------------------------

/** Formats one rule condition as human-readable text. */
function formatCondition(condition: Record<string, unknown>): string {
  const type = condition.conditionType;
  switch (type) {
    case "FormElementBased":
      return [
        condition.questionCode ?? "(question)",
        condition.type ?? "",
        condition.answerValue !== undefined && condition.answerValue !== null
          ? `"${condition.answerValue}"`
          : "",
      ]
        .filter(Boolean)
        .join(" ");
    case "FormStatusBased":
      return `Form status = ${condition.formStatus ?? "?"}`;
    case "WorkflowStatusBased":
      return `Workflow status = ${condition.workflowStatus ?? "?"}`;
    case "LoginAccountBased":
      return `Login account ${condition.type ?? ""}`.trim();
    case "TimeFrameBased":
      return `Time frame ${condition.timeFrameCode ?? condition.type ?? ""}`.trim();
    default:
      return JSON.stringify(condition);
  }
}

/** Renders a rule's conditions joined by its ANY/ALL mode. */
function formatConditions(rule: Record<string, unknown>): string {
  const conditions = Array.isArray(rule.conditions) ? rule.conditions : [];
  if (conditions.length === 0) {
    return "(no conditions — always applies)";
  }
  const joiner = rule.trueIfAny ? " OR " : " AND ";
  return conditions
    .map((c) => formatCondition(c as Record<string, unknown>))
    .join(joiner);
}

/** Reads a rule's human-facing fields (Type / Match / Conditions) into a map. */
function ruleFields(json: string | null | undefined): Record<string, string> {
  if (!json) {
    return {};
  }
  let rule: Record<string, unknown>;
  try {
    rule = JSON.parse(json);
  } catch {
    return {};
  }
  return {
    Type: displayValue(rule.ruleType),
    Target: displayValue(rule.elementQuestionCode),
    Match: rule.trueIfAny ? "Any condition" : "All conditions",
    Conditions: formatConditions(rule),
  };
}

/**
 * Field-by-field diff for a RuleChanged detail. One side is blank for an add/remove.
 */
function ruleDiffs(detail: FormDefinitionChangeDetailDto): FieldDiff[] {
  const before = ruleFields(detail.beforeJson);
  const after = ruleFields(detail.afterJson);
  const keys = ["Type", "Target", "Match", "Conditions"];
  const diffs: FieldDiff[] = [];
  for (const key of keys) {
    const b = before[key] ?? "";
    const a = after[key] ?? "";
    if (b !== a) {
      diffs.push({ field: key, before: b || "—", after: a || "—" });
    }
  }
  return diffs;
}

// --- Precomputed view model ----------------------------------------------
// Diffs are computed once per fetch (not on every render) — the table renders
// plain fields, so no JSON is parsed while re-rendering.

/** A detail enriched with its precomputed field diff (empty unless it has one). */
interface EnrichedDetail extends FormDefinitionChangeDetailDto {
  diffs: FieldDiff[];
}

interface EnrichedChange extends FormDefinitionChangeDto {
  detailRows: EnrichedDetail[];
}

const rows = computed<EnrichedChange[]>(() =>
  changes.value.map((change) => ({
    ...change,
    detailRows: (change.details ?? []).map((detail) => ({
      ...detail,
      diffs:
        detail.changeType === FormDefinitionChangeType.ELEMENT_CHANGED
          ? fieldDiffs(detail)
          : detail.changeType === FormDefinitionChangeType.RULE_CHANGED
            ? ruleDiffs(detail)
            : [],
    })),
  })),
);
</script>

<template>
  <div v-loading="loading" class="FormHistoryTab">
    <div class="history-toolbar">
      <el-button :icon="Refresh" @click="loadHistory">Refresh</el-button>
    </div>

    <el-table
      :data="rows"
      row-key="id"
      stripe
      border
      max-height="70vh"
      empty-text="No changes recorded yet."
    >
      <el-table-column label="When" width="200">
        <template #default="{ row }">
          {{ formatDate(row.changedAt, 1) }}
        </template>
      </el-table-column>

      <el-table-column label="User" prop="changedByLoginName" width="180">
        <template #default="{ row }">
          {{ row.changedByLoginName || "Unknown user" }}
        </template>
      </el-table-column>

      <el-table-column label="Event" width="150">
        <template #default="{ row }">
          <el-tag :type="kindTagType(row.kind)" disable-transitions>
            {{ kindLabel(row.kind) }}
          </el-tag>
        </template>
      </el-table-column>

      <el-table-column label="Changes" min-width="360">
        <template #default="{ row }">
          <div class="detail-list">
            <span v-if="row.detailRows.length === 0" class="no-detail">—</span>

            <div
              v-for="(detail, index) in row.detailRows"
              :key="index"
              class="detail-block"
            >
              <!-- A changed element: show only the field-by-field diff table. -->
              <el-table
                v-if="
                  detail.changeType ===
                    FormDefinitionChangeType.ELEMENT_CHANGED &&
                  detail.diffs.length
                "
                :data="detail.diffs"
                size="small"
                border
                class="field-diff"
              >
                <el-table-column prop="field" label="Field" width="160" />
                <el-table-column label="Before">
                  <template #default="{ row: diff }">
                    <span class="before">{{ diff.before }}</span>
                  </template>
                </el-table-column>
                <el-table-column label="After">
                  <template #default="{ row: diff }">
                    <span class="after">{{ diff.after }}</span>
                  </template>
                </el-table-column>
              </el-table>

              <!-- A changed rule: the description + a before/after table (conditions incl). -->
              <template
                v-else-if="
                  detail.changeType === FormDefinitionChangeType.RULE_CHANGED &&
                  detail.diffs.length
                "
              >
                <el-tag
                  size="small"
                  :type="detailTagType(detail.changeType)"
                  disable-transitions
                >
                  {{ detail.description }}
                </el-tag>
                <el-table
                  :data="detail.diffs"
                  size="small"
                  border
                  class="field-diff"
                >
                  <el-table-column prop="field" label="Field" width="120" />
                  <el-table-column label="Before">
                    <template #default="{ row: diff }">
                      <span class="before">{{ diff.before }}</span>
                    </template>
                  </el-table-column>
                  <el-table-column label="After">
                    <template #default="{ row: diff }">
                      <span class="after">{{ diff.after }}</span>
                    </template>
                  </el-table-column>
                </el-table>
              </template>

              <!-- Everything else (add/remove/meta): the short description. -->
              <el-tag
                v-else
                size="small"
                :type="detailTagType(detail.changeType)"
                disable-transitions
              >
                {{ detail.description }}
              </el-tag>
            </div>
          </div>
        </template>
      </el-table-column>
    </el-table>
  </div>
</template>

<style lang="less">
.FormHistoryTab {
  padding: 0 1em;
  .history-toolbar {
    display: flex;
    justify-content: flex-end;
    margin-bottom: 1em;
  }
  .detail-list {
    display: flex;
    flex-direction: column;
    gap: 0.6em;
    padding: 0.35em 0;
  }
  .no-detail {
    color: var(--el-text-color-secondary);
  }
  .detail-block {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
  }
  .field-diff {
    max-width: 100%;
    .before {
      color: var(--el-color-danger);
      text-decoration: line-through;
    }
    .after {
      color: var(--el-color-success);
    }
  }
}
</style>
